import React from "react";
import { useFormContext } from "react-hook-form";

/**
 * Select component that supports both react-hook-form and standalone usage.
 *
 * Props:
 * - name: string (required for react-hook-form)
 * - label: string
 * - options: array of { value, label }
 * - useForm: boolean (if true, integrates with react-hook-form)
 * - error: string (optional, for standalone error display)
 * - ...rest: other props for <select>
 */
const Select = ({
  name,
  label,
  options = [],
  useForm = false,
  error: errorProp,
  disabled,
  ...rest
}) => {
  // Always call the hook, only use its values if useForm is true
  const form = useFormContext?.();
  const register = form?.register;
  const errors = form?.formState?.errors;

  // Error message: from react-hook-form or prop
  const errorMsg =
    useForm && errors && errors[name]?.message
      ? errors[name]?.message
      : errorProp;

  return (
    <div className="sm:col-span-3">
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium mb-1 ${
            disabled ? "text-gray-400 dark:text-gray-500" : "text-gray-600 dark:text-gray-300"
          }`}
        >
          {label}
        </label>
      )}
      <div>
        <select
          id={name}
          name={name}
          disabled={disabled}
          className={`block justify-between gap-x-1.5 w-full rounded-md px-3 py-2.5 items-center text-sm leading-none font-normal border-0 transition-colors duration-150 ${
            disabled
              ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
              : "text-gray-500 dark:text-gray-200 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800"
          }`}
          {...(useForm && register ? register(name) : {})}
          {...rest}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {errorMsg && <p className="mt-2 text-sm text-danger">{errorMsg}</p>}
      </div>
    </div>
  );
};

export { Select };
